// AdminShell — the console chrome: sidebar, top bar, palette, notifications.
//
// Extracted out of `_authed.tsx` so the route file keeps ONLY the auth
// `beforeLoad` and the staff gate. That boundary is deliberate: auth is the one
// piece of the shell that must not churn while the UI is redesigned.
//
// What this adds over the previous inline shell:
//   · collapsible sidebar, persisted per browser
//   · a REAL global search (the old header input was decorative) that opens
//     the command palette rather than pretending to search in place
//   · ⌘K command palette
//   · notification centre for things that need staff attention
//   · an environment badge — this repo has already shipped the dev database to
//     production once; "which environment am I editing" should never be a guess

import { cn } from '@fajrarabi/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CommandPalette, useCommandPaletteShortcut, type CommandItem } from './CommandPalette';
import { Icon, type IconName } from './Icon';
import { GROUP_TONE, TONE_TEXT, TONE_TILE, type IconTone } from './iconTone';

import { API_BASE } from '~/lib/api';
import { appOrigin, appSignInUrl, logout, type MeUser } from '~/lib/auth';

// ─── Navigation model ────────────────────────────────────────────────────────

export interface NavLink {
  to: string;
  label: string;
  icon: IconName;
  /** Only shown to these roles. Omit for "any staff role". */
  roles?: readonly string[];
}

export interface NavGroup {
  heading: string;
  /** The big glyph beside the group title. */
  icon: IconName;
  items: readonly NavLink[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    heading: 'Overview',
    icon: 'grid',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: 'grid' },
      { to: '/reports', label: 'Reports', icon: 'chart' },
      { to: '/audit', label: 'Audit log', icon: 'history' },
    ],
  },
  {
    heading: 'People',
    icon: 'users',
    items: [
      { to: '/users', label: 'Users', icon: 'users' },
      { to: '/families', label: 'Families', icon: 'family' },
      { to: '/schools', label: 'Schools', icon: 'school' },
    ],
  },
  {
    heading: 'Content',
    icon: 'book',
    items: [
      { to: '/content', label: 'Curriculum', icon: 'book' },
      { to: '/pages', label: 'Pages', icon: 'page' },
      { to: '/blog', label: 'Blog', icon: 'edit' },
      { to: '/blog-categories', label: 'Blog categories', icon: 'tag' },
      // /translations-legacy is intentionally absent — reachable by URL only
      // until the M9 cutover (see that route's header comment).
      { to: '/languages', label: 'Languages', icon: 'languages' },
      { to: '/translations', label: 'Translations', icon: 'globeAlt' },
      { to: '/media', label: 'Media', icon: 'image' },
    ],
  },
  {
    heading: 'Billing',
    icon: 'money',
    items: [
      { to: '/plans', label: 'Plans', icon: 'card' },
      { to: '/subscriptions', label: 'Subscriptions', icon: 'refresh' },
      { to: '/payments', label: 'Payments', icon: 'money' },
      { to: '/coupons', label: 'Coupons', icon: 'tag' },
      // Records first, diagnostics last (M7f). Amber follows the Billing group.
      { to: '/webhooks', label: 'Webhooks', icon: 'webhook' },
    ],
  },
  {
    heading: 'System',
    icon: 'settings',
    items: [
      { to: '/announcements', label: 'Announcements', icon: 'megaphone' },
      { to: '/email', label: 'Email', icon: 'envelope' },
      { to: '/flags', label: 'Feature flags', icon: 'flag' },
      { to: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

const SIDEBAR_KEY = 'fajr.admin.sidebar.collapsed';
const GROUPS_KEY = 'fajr.admin.sidebar.groups';

/**
 * Which deployment this admin is pointed at, derived from the API base rather
 * than the page host — what matters is which DATABASE is being written.
 */
export function environmentLabel(): { label: string; tone: 'prod' | 'staging' | 'local' } | null {
  if (API_BASE.includes('staging')) return { label: 'Staging', tone: 'staging' };
  if (API_BASE.includes('localhost') || API_BASE.includes('127.0.0.1'))
    return { label: 'Local', tone: 'local' };
  // Production is deliberately unlabelled: a badge on every screen in the
  // normal case becomes furniture and stops being read.
  return null;
}

export interface AdminShellProps {
  me: MeUser;
  children: React.ReactNode;
}

export function AdminShell({ me, children }: AdminShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      // Private mode / blocked storage — a preference is not worth throwing over.
      return false;
    }
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  // heading → open. Only an explicit `false` collapses, so a NEW group added
  // later defaults to open rather than being silently hidden from operators
  // who already have a stored preference.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(GROUPS_KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_KEY, JSON.stringify(openGroups));
    } catch {
      /* ignore */
    }
  }, [openGroups]);

  const toggleGroup = useCallback((heading: string) => {
    setOpenGroups((prev) => ({ ...prev, [heading]: prev[heading] === false }));
  }, []);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteShortcut(openPalette);

  const commands = useMemo<CommandItem[]>(() => {
    const sections = NAV_GROUPS.flatMap((group) =>
      group.items.map((item) => ({
        id: `nav:${item.to}`,
        label: item.label,
        hint: group.heading,
        icon: item.icon,
        group: 'Go to' as const,
        onSelect: () => void navigate({ to: item.to }),
      })),
    );

    const actions: CommandItem[] = [
      {
        id: 'action:new-blog-post',
        label: 'New blog post',
        icon: 'plus',
        group: 'Actions',
        keywords: 'create write article',
        onSelect: () => void navigate({ to: '/blog/new' }),
      },
      {
        id: 'action:subscriptions',
        label: 'Find a subscription',
        icon: 'card',
        group: 'Actions',
        keywords: 'comp grant plan access',
        onSelect: () => void navigate({ to: '/subscriptions' }),
      },
      {
        id: 'action:payments',
        label: 'Find a payment',
        icon: 'money',
        group: 'Actions',
        keywords: 'invoice refund receipt',
        onSelect: () => void navigate({ to: '/payments' }),
      },
    ];

    return [...actions, ...sections];
  }, [navigate]);

  return (
    <div className="min-h-dvh bg-admin-canvas">
      <Sidebar
        mobileOpen={mobileOpen}
        collapsed={collapsed}
        openGroups={openGroups}
        onToggleGroup={toggleGroup}
        onClose={() => setMobileOpen(false)}
        onToggleCollapse={() => setCollapsed((c) => !c)}
      />

      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-admin-900/30 lg:hidden"
        />
      )}

      <div
        className={cn(
          'flex min-h-dvh flex-col transition-[padding]',
          collapsed ? 'lg:ps-[4.5rem]' : 'lg:ps-72',
        )}
      >
        <Header me={me} onMenu={() => setMobileOpen(true)} onSearch={openPalette} />
        <main className="flex-1 overflow-auto px-4 py-6 md:px-6 lg:px-8">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} items={commands} />
    </div>
  );
}

function Sidebar({
  mobileOpen,
  collapsed,
  openGroups,
  onToggleGroup,
  onClose,
  onToggleCollapse,
}: {
  mobileOpen: boolean;
  collapsed: boolean;
  /** heading → open. Absent means open; only explicit `false` collapses. */
  openGroups: Record<string, boolean>;
  onToggleGroup: (heading: string) => void;
  onClose: () => void;
  onToggleCollapse: () => void;
}) {
  const env = environmentLabel();

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-40 flex flex-col border-e border-admin-200 bg-white transition-[transform,width] duration-200 lg:translate-x-0',
        collapsed ? 'w-72 lg:w-[4.5rem]' : 'w-72',
        mobileOpen
          ? 'translate-x-0'
          : '-translate-x-full rtl:translate-x-full lg:rtl:translate-x-0',
      )}
    >
      <div className="flex h-16 items-center gap-2.5 px-5">
        <img src="/logo.png" alt="" className="h-9 w-9 shrink-0 rounded-lg" />
        <div className={cn('leading-tight', collapsed && 'lg:hidden')}>
          <p className="text-sm font-semibold text-admin-900">Fajr Arabi</p>
          <p className="text-[11px] text-admin-400">Staff console</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="ms-auto grid h-8 w-8 place-items-center rounded-lg text-admin-500 hover:bg-admin-100 lg:hidden"
        >
          <Icon name="close" className="h-4 w-4" />
        </button>
      </div>

      {env && !collapsed && (
        <div className="mx-4 mb-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset',
              env.tone === 'staging'
                ? 'bg-warning-50 text-warning-700 ring-warning-500/30'
                : 'bg-brand-50 text-brand-700 ring-brand-500/30',
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
            {env.label} environment
          </span>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {NAV_GROUPS.map((group) => (
          <NavGroupSection
            key={group.heading}
            group={group}
            collapsed={collapsed}
            open={openGroups[group.heading] !== false}
            onToggle={() => onToggleGroup(group.heading)}
            onNavigate={onClose}
          />
        ))}
      </nav>
      <div className="hidden border-t border-admin-200 p-2 lg:block">
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-admin-500 transition-colors hover:bg-admin-100',
            collapsed && 'justify-center px-2',
          )}
        >
          <Icon name="sidebar" className="h-4 w-4" />
          <span className={cn(collapsed && 'hidden')}>Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function Header({
  me,
  onMenu,
  onSearch,
}: {
  me: MeUser;
  onMenu: () => void;
  onSearch: () => void;
}) {
  const name = me.displayName ?? me.email ?? '';
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleLogout() {
    await logout();
    window.location.assign(appSignInUrl(`${window.location.origin}/dashboard`));
  }

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-admin-200 bg-white/90 px-4 backdrop-blur md:px-6 lg:px-8">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        className="grid h-9 w-9 place-items-center rounded-lg text-admin-600 hover:bg-admin-100 lg:hidden"
      >
        <Icon name="menu" />
      </button>

      {/*
        A BUTTON, not an input.
        The old shell rendered a real <input> here that searched nothing —
        it was decorative, and it was also the documented automation trap
        (`document.querySelector('input')` finds the sidebar search, not the
        page's first form field). A button that opens the palette is honest
        about what it does and removes the phantom input entirely.
      */}
      <button
        type="button"
        onClick={onSearch}
        className="hidden max-w-md flex-1 items-center gap-2 rounded-lg border border-admin-200 bg-admin-50 px-3 py-2 text-start text-sm text-admin-400 transition-colors hover:bg-admin-100 sm:flex"
      >
        <Icon name="search" className="h-4 w-4" />
        <span className="flex-1">Search…</span>
        <kbd className="rounded border border-admin-200 bg-white px-1.5 py-0.5 text-[10px] font-medium">
          ⌘K
        </kbd>
      </button>

      <button
        type="button"
        onClick={onSearch}
        aria-label="Search"
        className="grid h-9 w-9 place-items-center rounded-lg text-admin-600 hover:bg-admin-100 sm:hidden"
      >
        <Icon name="search" />
      </button>

      <div className="ms-auto flex items-center gap-2">
        <a
          href={appOrigin()}
          target="_blank"
          rel="noreferrer"
          className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-admin-600 transition-colors hover:bg-admin-100 md:inline-flex"
        >
          View site
          <Icon name="external" className="h-3.5 w-3.5" />
        </a>

        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-admin-100"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-500 text-sm font-semibold text-white">
              {initial}
            </span>
            <span className="hidden text-sm text-admin-700 sm:inline">{name}</span>
            <Icon name="chevronDown" className="h-4 w-4 text-admin-400" />
          </button>

          {menuOpen && (
            <>
              <button
                type="button"
                aria-hidden
                tabIndex={-1}
                onClick={() => setMenuOpen(false)}
                className="fixed inset-0 z-10 cursor-default"
              />
              <div
                role="menu"
                className="absolute end-0 top-full z-20 mt-1 w-56 rounded-xl border border-admin-200 bg-white p-1.5 shadow-lg"
              >
                <div className="border-b border-admin-100 px-3 py-2">
                  <p className="truncate text-sm font-medium text-admin-900">{name}</p>
                  <p className="truncate text-xs text-admin-500">{me.email}</p>
                  {me.roles.length > 0 && (
                    <p className="mt-1 truncate text-[11px] uppercase tracking-wide text-admin-400">
                      {me.roles.join(' · ')}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50"
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * One collapsible nav group.
 *
 * Owner ruling 2026-08-25: a **big** icon beside the group title, **small**
 * icons on the links, and each group collapsible. The size difference is doing
 * real work — it makes the group headings scannable as landmarks while the
 * links stay a quiet list underneath.
 *
 * Colour comes from `GROUP_TONE`, so a domain keeps the same hue everywhere it
 * appears in the console (see iconTone.ts for why that rule matters).
 *
 * When the sidebar is COLLAPSED the group cannot collapse further — there is
 * no title left to click — so it renders as a plain icon rail.
 */
function NavGroupSection({
  group,
  collapsed,
  open,
  onToggle,
  onNavigate,
}: {
  group: NavGroup;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const tone: IconTone = GROUP_TONE[group.heading] ?? 'slate';
  const expanded = collapsed || open;

  return (
    <div className="mb-2">
      {collapsed ? (
        // Collapsed rail: the group icon becomes a silent divider/label.
        <div className="mb-1 flex justify-center py-1" title={group.heading}>
          <span className={cn('grid h-7 w-7 place-items-center rounded-lg', TONE_TILE[tone])}>
            <Icon name={group.icon} className="h-4 w-4" />
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-start transition-colors hover:bg-admin-50"
        >
          <span
            className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', TONE_TILE[tone])}
          >
            <Icon name={group.icon} className="h-[18px] w-[18px]" />
          </span>
          <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-admin-500">
            {group.heading}
          </span>
          <Icon
            name="chevronDown"
            className={cn(
              'h-4 w-4 text-admin-300 transition-transform motion-reduce:transition-none',
              !open && '-rotate-90 rtl:rotate-90',
            )}
          />
        </button>
      )}

      {expanded && (
        <ul
          className={cn(
            'space-y-0.5',
            !collapsed && 'ms-2.5 mt-0.5 border-s border-admin-100 ps-2.5',
          )}
        >
          {group.items.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                onClick={onNavigate}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-100 [&.active]:bg-brand-50 [&.active]:font-semibold [&.active]:text-brand-700',
                  collapsed && 'lg:justify-center lg:px-2',
                )}
                activeProps={{ className: 'active' }}
              >
                {/* Small link icon — deliberately 16px against the group's 18px
                    in a 32px tile, so the hierarchy reads at a glance. */}
                <span
                  className={cn(
                    'shrink-0 transition-colors',
                    TONE_TEXT[tone],
                    'opacity-70 group-hover:opacity-100 [.active_&]:opacity-100',
                  )}
                >
                  <Icon name={item.icon} className="h-4 w-4" />
                </span>
                <span className={cn('truncate', collapsed && 'lg:hidden')}>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
